import api from '@/apis'
import Vuetify from '@/plugins/vuetify'

// Per-device serialized PUT chains. Keeps same-device PUTs ordered so out-of-order
// completions can't overwrite later edits, while different-device saves run in parallel.
// Fetches only await their own device's chain, so reopening on device A waits behind A's
// still-in-flight PUT even if a different device's save was the latest enqueue.
const bottomMenuSaveChainByDevice = new Map()
// Monotonic fetch session so a stale load (e.g. dialog closed + reopened after device switch)
// can't commit its response into state after a newer fetch has already run. Also bumped on
// BottomMenu unmount (via invalidateBottomMenuFetch) so a fetch that outlives its editor
// can't flip the shared interfaceMngtDialog.loading flag while another editor (e.g. carousel
// banner) is mid-fetch and relying on that same flag to gate its render.
let bottomMenuFetchSession = 0
// Device pinned at BottomMenu mount. All bottom-menu actions (PUT, upload, fetch) read this
// instead of rootState.auth.selected_device because a device switch updates the global
// selection *before* the /interface page unmounts — so a pending blur-save or the unmount
// flush would otherwise send device A's items to device B.
let bottomMenuEditorDevice = null
// Flusher for the BottomMenu component's local debounced save. Other actions
// (e.g. Add Item) trigger this before enqueuing their own PUT so a slower
// debounce can't fire later on the serialized chain and overwrite the result.
let bottomMenuDraftFlusher = null
// Returns the items snapshot with uncommitted payload drafts overlaid. Other
// actions (e.g. Add Item) call this before PUT so typed-but-unblurred text
// isn't silently overwritten by the old server payload under the adjacent row.
let bottomMenuDraftApplier = null

// Single seam for "what device does this editor operation belong to?" — used by fetch/update/upload.
// Prefers the editor-pinned device over the live global selection so an operation queued before a
// device switch still targets the editor's original device.
const editorDeviceOf = (rootState) => bottomMenuEditorDevice ?? rootState.auth.selected_device

// Per-component serialized PUT chains for payload saves. Rapid changes (e.g. the
// weather region toggled HK→TW→HK before the first request returns) would
// otherwise race, and whichever response landed last — not the user's last
// choice — would win on both the server and in local state. Serializing keeps
// them ordered so the final selection is the one that persists.
const componentPayloadSaveChainById = new Map()

export default {
  state: {
    visibility: false,
    section: null,
    view: {
      page: 1,
      component: null
    },
    loading: false,

    carousel_banner: {},
    bottom_menu: { items: [] },
    bottom_menu_loaded: false,
    // Monotonic invalidation token. Bumped on: (a) BottomMenu editor unmount
    // so handlers don't outlive the editor or PUT into a different device
    // after a switch; (b) updateBottomMenu task failure so in-flight uploads
    // (and already-enqueued PUTs on the save chain) whose items were built
    // from optimistic state the server just rejected can't re-apply the
    // failed mutation. Handlers snapshot this before awaits and bail if it
    // moved; the task itself also re-checks at start.
    bottom_menu_editor_session: 0
  },
  mutations: {
    INIT_INTERFACE_MNGT_DIALOG(state, payload) {
      state.visibility = true
      state.section = payload
    },
    RESET_INTERFACE_MNGT_DIALOG(state) {
      state.visibility = false
      setTimeout(() => {
        state.view = {
          page: 1,
          component: null
        }
        state.section = null
        state.bottom_menu_loaded = false
      }, 500)
    },
    SET_INTERFACE_MNGT_DIALOG_VIEW(state, payload) {
      // 1 -> Overview, 2 -> Edit Component Details
      const { view, component = null } = payload
      state.view = {
        page: view === 'overview' ? 1 : 2,
        component: component
      }
    },
    SET_INTERFACE_MNGT_DIALOG_LOADING(state, payload) {
      state.loading = payload
    },

    // Carousel banner
    SET_INTERFACE_MNGT_DIALOG_CAROUSEL_BANNER(state, payload) {
      state.carousel_banner = payload
    },

    // Bottom menu
    SET_INTERFACE_MNGT_DIALOG_BOTTOM_MENU(state, payload) {
      state.bottom_menu = payload
    },
    SET_INTERFACE_MNGT_DIALOG_BOTTOM_MENU_LOADED(state, payload) {
      state.bottom_menu_loaded = payload
    },
    BUMP_BOTTOM_MENU_EDITOR_SESSION(state) {
      state.bottom_menu_editor_session++
    }
  },
  actions: {
    // Section

    async updateInterfaceGeneralSection({ state, commit }) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })

        let selected_comp_ids = []
        state.section.components.forEach((component) => {
          if (component.is_enabled) selected_comp_ids.push(component.id)
        })

        const params = { section_id: state.section.id, selected_comp_ids: selected_comp_ids }
        const response = await api.updateSection({ params: params })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.update', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },

    // Persists a single general-section component's payload (e.g. the weather
    // region). Saves for the same component are serialized (see
    // componentPayloadSaveChainById) so rapid changes land in order and the last
    // selection wins; on success the local component is mutated in place so
    // reopening the editor reflects the saved value without a refetch.
    async updateInterfaceComponentPayload({ state, commit }, payload) {
      const { component_id } = payload
      // Snapshot the section id and component reference at dispatch time. A queued
      // save may run after the user closes the dialog or switches section/device,
      // at which point RESET_INTERFACE_MNGT_DIALOG has nulled state.section —
      // dereferencing it live would throw or send the wrong section id. The
      // component object is the same one held in state.section.components, so
      // mutating its payload stays correct even once the dialog is gone.
      const section_id = state.section.id
      const component = state.section.components.find((c) => c.id === component_id)
      const task = async () => {
        try {
          commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })

          const params = { section_id: section_id, comp_payload: { id: component_id, payload: payload.payload } }
          const response = await api.updateSection({ params: params })
          if (response.data.success) {
            if (component) component.payload = payload.payload
            commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.update', ''), icon: 'check', timeout: '2000' })
          } else throw Error(response.data.message)
        } catch (error) {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
        }
      }
      const prev = componentPayloadSaveChainById.get(component_id) ?? Promise.resolve()
      const next = prev.then(task, task)
      componentPayloadSaveChainById.set(
        component_id,
        next.catch(() => {})
      )
      return next
    },

    // Carousel Banner
    async fetchCarouselBannerList({ commit }) {
      try {
        commit('SET_INTERFACE_MNGT_DIALOG_LOADING', true)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.loading', ''), loading: true })
        const response = await api.getCarouselBannerList()
        if (response.data.success) {
          commit('SET_INTERFACE_MNGT_DIALOG_CAROUSEL_BANNER', response.data.data)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.load', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_INTERFACE_MNGT_DIALOG_LOADING', false)
      }
    },

    async updateCarouselBanner({ rootState, commit }, payload) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })

        const params = { device_id: rootState.auth.selected_device, ...payload.param }
        const response = await api.updateCarouselBanner({ params: params })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.update', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },

    async uploadCarouselBannerMedia({ rootState, commit, dispatch }, payload) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.uploading', ''), loading: true })

        const form_data = new FormData()
        form_data.append('device_id', rootState.auth.selected_device)
        payload.files.forEach((file) => {
          form_data.append('files', file)
        })

        const response = await api.uploadCarouselBannerMediaFile({ params: form_data })
        if (response.data.success) {
          dispatch('fetchCarouselBannerList')
          commit('SET_ADD_CAROUSEL_BANNER_DIALOG_VISIBILITY', false)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.upload', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },

    async deleteCarouselBanner({ rootState, state, commit, dispatch }, payload) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.deleting', ''), loading: true })
        let params = { device_id: rootState.auth.selected_device, image_id: payload.image_id }

        const response = await api.deleteCarouselBanner({ params: params })
        if (response.data.success) {
          dispatch('fetchCarouselBannerList', state.current_media_type)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.delete', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },

    // Bottom Menu
    registerBottomMenuDraftFlusher(_, fn) {
      bottomMenuDraftFlusher = typeof fn === 'function' ? fn : null
    },
    flushBottomMenuDrafts() {
      if (bottomMenuDraftFlusher) bottomMenuDraftFlusher()
    },
    registerBottomMenuDraftApplier(_, fn) {
      bottomMenuDraftApplier = typeof fn === 'function' ? fn : null
    },
    applyBottomMenuDrafts(_, items) {
      return bottomMenuDraftApplier ? bottomMenuDraftApplier(items) : items
    },
    invalidateBottomMenuFetch() {
      bottomMenuFetchSession++
    },
    registerBottomMenuEditorDevice(_, device_id) {
      bottomMenuEditorDevice = device_id
    },

    async fetchBottomMenuList({ rootState, commit }) {
      const session = ++bottomMenuFetchSession
      // Always wipe items before fetching: updateBottomMenu bumps the editor
      // session on failure, which invalidates any in-flight upload's post-
      // resolve check — so preserving optimistic state for a "resumed upload"
      // would only serve stale rows to handlers that are about to bail anyway.
      commit('SET_INTERFACE_MNGT_DIALOG_BOTTOM_MENU', { items: [] })
      commit('SET_INTERFACE_MNGT_DIALOG_BOTTOM_MENU_LOADED', false)
      commit('SET_INTERFACE_MNGT_DIALOG_LOADING', true)
      const device_id = editorDeviceOf(rootState)
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.loading', ''), loading: true })
        // Refetch if the save chain advances during GET — otherwise the
        // response reflects pre-PUT state and publishing it would overwrite
        // the retry. Other devices' chains run in parallel and can't affect
        // this device's server state, so only this device's chain matters.
        let response
        let chain_snapshot
        do {
          let pending = bottomMenuSaveChainByDevice.get(device_id)
          while (pending) {
            await pending.catch(() => {})
            if (session !== bottomMenuFetchSession) return
            const next = bottomMenuSaveChainByDevice.get(device_id)
            if (next === pending) break
            pending = next
          }
          chain_snapshot = bottomMenuSaveChainByDevice.get(device_id)
          response = await api.getBottomMenuList({ device_id })
          if (session !== bottomMenuFetchSession) return
        } while (bottomMenuSaveChainByDevice.get(device_id) !== chain_snapshot)
        if (response.data.success) {
          // Backend returns items as {} (object) when empty instead of [],
          // which breaks every downstream consumer that spreads/iterates items.
          const data = response.data.data ?? {}
          const normalized = { ...data, items: Array.isArray(data.items) ? data.items : [] }
          commit('SET_INTERFACE_MNGT_DIALOG_BOTTOM_MENU', normalized)
          commit('SET_INTERFACE_MNGT_DIALOG_BOTTOM_MENU_LOADED', true)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.load', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        if (session !== bottomMenuFetchSession) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        // Only the current session owns the loading flag.
        if (session === bottomMenuFetchSession) {
          commit('SET_INTERFACE_MNGT_DIALOG_LOADING', false)
        }
      }
    },

    async updateBottomMenu({ rootState, state, commit }, payload) {
      // Editor-pinned device so a pending blur-save or unmount flush still PUTs to the
      // editor's original device even if the user already switched the global selection
      // via LeftBar while dispatch was queued.
      const device_id = editorDeviceOf(rootState)
      const editor_session_at_enqueue = state.bottom_menu_editor_session
      // Clone at enqueue time so a queued PUT can't observe mutations the caller
      // makes to its source array/items while this task waits its turn.
      const items = payload.items.map((item) => ({ ...item }))
      const task = async () => {
        // Skip if an earlier task on this chain failed (and bumped the editor
        // session) or the editor unmounted since enqueue — this PUT's items
        // were built from optimistic state the server has now rejected or
        // that belongs to a closed editor instance.
        if (state.bottom_menu_editor_session !== editor_session_at_enqueue) return false
        try {
          commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.updating', ''), loading: true })
          const params = { device_id, items }
          const response = await api.updateBottomMenu({ params: params })
          if (response.data.success) {
            commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.update', ''), icon: 'check', timeout: '2000' })
            return true
          } else throw Error(response.data.message)
        } catch (error) {
          // Only bump if the session still matches our enqueue point. If it
          // already advanced, the previous editor unmounted mid-HTTP and any
          // freshly-mounted editor (possibly on a different device) now owns
          // the current session — bumping again would invalidate *its*
          // in-flight uploads/saves for a failure that belonged to a dead
          // editor. Handlers from our editor already bail on their own
          // unmount-bump; no need to re-invalidate.
          if (state.bottom_menu_editor_session === editor_session_at_enqueue) {
            commit('BUMP_BOTTOM_MENU_EDITOR_SESSION')
          }
          commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
          return false
        }
      }
      const prev = bottomMenuSaveChainByDevice.get(device_id) ?? Promise.resolve()
      const next = prev.then(task, task)
      bottomMenuSaveChainByDevice.set(
        device_id,
        next.catch(() => {})
      )
      return next
    },

    async uploadBottomMenuImage({ rootState, commit }, payload) {
      try {
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.uploading', ''), loading: true })
        const form_data = new FormData()
        // Editor-pinned device so a replace upload in flight during a device switch doesn't
        // get stored under the newly-selected device's scope.
        form_data.append('device_id', editorDeviceOf(rootState))
        form_data.append('file', payload.file)

        const response = await api.uploadBottomMenuImage({ params: form_data })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.upload', ''), icon: 'check', timeout: '2000' })
          return response.data.data.url
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
        return null
      }
    }
  }
}
